<div class="modal-header">
  <h5 class="modal-title">{{ 'AI_PARSER_SUBMIT_TO_ERP' | translate }}</h5>
  <button type="button" class="btn-close" (click)="cancel()" [attr.aria-label]="'CANCEL' | translate"></button>
</div>
<div class="modal-body">
  <!-- Hero figure: rows · units over the order total -->
  <div class="hero">
    <div class="hero-caption">
      {{ (summary.rowCount === 1 ? 'AI_PARSER_SUMMARY_ROWS_ONE' : 'AI_PARSER_SUMMARY_ROWS') | translate: { count: summary.rowCount } }}
      <span aria-hidden="true">·</span>
      {{ (summary.totalQuantity === 1 ? 'AI_PARSER_SUMMARY_UNITS_ONE' : 'AI_PARSER_SUMMARY_UNITS') | translate: { count: summary.totalQuantity } }}
    </div>
    <div class="hero-total" *ngIf="summary.totalValue !== null; else noTotalHero">
      {{ summary.totalValue | number: '1.2-2' }} {{ summary.currencyCode }}
    </div>
    <ng-template #noTotalHero>
      <div class="hero-total hero-total-empty" aria-hidden="true">—</div>
      <div class="hero-note">{{ 'AI_PARSER_NOT_AVAILABLE' | translate }}</div>
    </ng-template>
  </div>

  <!-- Definition grid -->
  <dl class="summary-grid">
    <div class="def">
      <dt>{{ 'AI_PARSER_CUSTOMER' | translate }}</dt>
      <dd>
        <ng-container *ngIf="summary.deliveryCustomerId; else emptyValue">
          {{ summary.deliveryCustomerName || summary.deliveryCustomerId }}
          <span class="value-caption" *ngIf="summary.deliveryCustomerName">{{ summary.deliveryCustomerId }}</span>
        </ng-container>
      </dd>
    </div>
    <div class="def" *ngIf="summary.senderEmail">
      <dt>{{ 'AI_PARSER_SENDER_EMAIL' | translate }}</dt>
      <dd>{{ summary.senderEmail }}</dd>
    </div>
    <div class="def" *ngIf="summary.billingCustomerId && summary.billingCustomerId !== summary.deliveryCustomerId">
      <dt>{{ 'AI_PARSER_BILLING_CUSTOMER' | translate }}</dt>
      <dd>{{ summary.billingCustomerId }}</dd>
    </div>
    <div class="def" *ngFor="let field of summary.orderFields">
      <dt>{{ field.labelKey | translate }}</dt>
      <dd>
        <ng-container *ngIf="field.value; else fieldMissing">{{ field.value }}</ng-container>
        <ng-template #fieldMissing>
          <span class="value-missing">{{ 'AI_PARSER_SUBMIT_FIELD_MISSING' | translate }}</span>
        </ng-template>
      </dd>
    </div>
    <div class="def def-wide">
      <dt>{{ 'AI_PARSER_DELIVERY_ADDRESS' | translate }}</dt>
      <dd>
        <ng-container *ngIf="summary.deliveryAddress.length > 0; else addressMissing">
          <details class="address-details">
            <summary>
              {{ addressSummaryLine }}
              <span class="address-toggle">— {{ 'AI_PARSER_SHOW_ADDRESS' | translate }}</span>
            </summary>
            <div class="address-lines">
              <div *ngFor="let line of summary.deliveryAddress">{{ line }}</div>
            </div>
          </details>
          <span class="value-caption" *ngIf="summary.deliveryAddressFromCustomer">{{
            'AI_PARSER_SUBMIT_ADDRESS_FROM_CUSTOMER' | translate
          }}</span>
        </ng-container>
        <ng-template #addressMissing>
          <span class="value-missing">{{ 'AI_PARSER_SUBMIT_FIELD_MISSING' | translate }}</span>
        </ng-template>
      </dd>
    </div>
  </dl>
  <p class="erp-defaults-note" *ngIf="hasErpDefaults">{{ 'AI_PARSER_SUBMIT_ERP_DEFAULTS_NOTE' | translate }}</p>

  <!-- Receipt rows -->
  <table class="receipt-table" *ngIf="summary.rows.length > 0">
    <thead>
      <tr>
        <th class="text-end col-num">#</th>
        <th>{{ 'AI_PARSER_PRODUCT' | translate }}</th>
        <th class="text-end">{{ 'AI_PARSER_QUANTITY' | translate }}</th>
        <th class="text-end">{{ 'AI_PARSER_UNIT_PRICE' | translate }}</th>
        <th class="text-end">{{ 'AI_PARSER_LINE_TOTAL' | translate }}</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let row of pagedRows">
        <td class="text-end row-num">{{ row.rowNumber }}</td>
        <td class="product-cell">
          <span class="product-id">{{ row.productId }}</span>
          <span class="product-name" *ngIf="row.productName" [attr.title]="row.productName">{{ row.productName }}</span>
        </td>
        <td class="text-end">{{ row.quantity }}</td>
        <td class="text-end">
          <ng-container *ngIf="row.unitPrice !== null; else noUnitPrice">
            {{ row.unitPrice | number: '1.2-2' }} {{ row.currencyCode
            }}<sup
              *ngIf="row.usedDocumentPrice"
              class="doc-marker"
              [ngbTooltip]="
                'AI_PARSER_DOC_MARKER_TOOLTIP'
                  | translate: { price: (row.customerPrice | number: '1.2-2') + ' ' + row.currencyCode }
              "
              >{{ 'AI_PARSER_DOC_MARKER' | translate }}</sup
            >
          </ng-container>
          <ng-template #noUnitPrice>
            <span class="cell-empty" [ngbTooltip]="'AI_PARSER_NO_CUSTOMER_PRICE' | translate">—</span>
          </ng-template>
        </td>
        <td class="text-end">
          <ng-container *ngIf="row.lineTotal !== null; else emptyValue">{{ row.lineTotal | number: '1.2-2' }}</ng-container>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr class="total-row">
        <td colspan="4">{{ 'AI_PARSER_TOTAL' | translate }}</td>
        <td class="text-end">
          <ng-container *ngIf="summary.totalValue !== null; else emptyValue">
            {{ summary.totalValue | number: '1.2-2' }} {{ summary.currencyCode }}
          </ng-container>
        </td>
      </tr>
    </tfoot>
  </table>

  <!-- Receipt pagination for long orders -->
  <div class="receipt-pagination" *ngIf="summary.rows.length > pageSize">
    <span class="page-range">
      {{ 'AI_PARSER_PAGE_RANGE' | translate: { from: pageFrom, to: pageTo, total: summary.rows.length } }}
    </span>
    <span class="page-buttons">
      <button
        type="button"
        class="btn btn-link btn-sm"
        [disabled]="page === 0"
        [attr.aria-label]="'AI_PARSER_PAGE_PREV' | translate"
        (click)="prevPage()"
      >
        <i class="fas fa-chevron-left" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="btn btn-link btn-sm"
        [disabled]="page >= pageCount - 1"
        [attr.aria-label]="'AI_PARSER_PAGE_NEXT' | translate"
        (click)="nextPage()"
      >
        <i class="fas fa-chevron-right" aria-hidden="true"></i>
      </button>
    </span>
  </div>

  <!-- Price-source footnote, only when deviations were resolved -->
  <p class="deviation-footnote" *ngIf="summary.priceDeviations.length > 0">
    {{ 'AI_PARSER_DEVIATION_FOOTNOTE' | translate: { document: documentPriceCount, customer: customerPriceCount } }}
  </p>

  <!-- Invalid rows block correctness — keep this loud -->
  <div *ngIf="summary.invalidRowCount > 0" class="receipt-note">
    {{
      (summary.invalidRowCount === 1 ? 'AI_PARSER_INVALID_ROWS_WARNING_ONE' : 'AI_PARSER_INVALID_ROWS_WARNING')
        | translate: { count: summary.invalidRowCount }
    }}
  </div>
</div>
<div class="modal-footer">
  <button type="button" class="btn btn-link text-secondary" (click)="cancel()" [disabled]="isSubmitting">
    {{ 'CANCEL' | translate }}
  </button>
  <button type="button" class="btn btn-primary" (click)="confirm()" [disabled]="isSubmitting">
    <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
    {{ (isSubmitting ? 'AI_PARSER_SUBMITTING' : 'AI_PARSER_SUBMIT_ORDER') | translate }}
  </button>
</div>

<ng-template #emptyValue><span class="cell-empty">—</span></ng-template>
